import { useUniquePossibilities } from "@/hooks/useUniquePossibilities";
import { ReactNode } from "react";
import { useLocation, useParams } from "react-router";

type WithExtraFooterProps = {
  children?: ReactNode;
};

const balDisclaimer =
  "This data is for informational purposes only and not intended to replace the statements, confirms, or 1099 forms distributed by the custodian of your assets.";
const upDisclaimer =
  "Information contained in this report has been obtained from sources that we believe reliable. We assume, without independent investigation, that such information is complete and accurate. This hypothetical illustration is gross of federal and state taxes. Accordingly, investors are urged to consult with their own tax/legal advisors. AdviceOne Advisory Services LLC is not engaged in rendering tax or legal advice. Rates of return are for illustrative purposes only and do not represent any specific investment product or strategy. All pre-tax inflows, (including Social Security, Pensions, future work income, investment capital additions) and outflows as well as return on investment are calculated as occurring on the first day of the year in which they appear. This will have the effect of overstating returns during years of net outflows.";
const npvDisclaimer =
  "Information contained in this report has been obtained from sources that we believe reliable. We assume, without independent investigation, that such information is complete and accurate. This hypothetical illustration is gross of federal and state taxes. Accordingly, investors are urged to consult with their own tax/legal advisors. AdviceOne Advisory Services LLC is not engaged in rendering tax or legal advice. Rates of return are for illustrative purposes only and do not represent any specific investment product or strategy. All pre-tax inflows, (including Social Security, Pensions, future work income, investment capital additions) and outflows as well as return on investment are calculated as occurring on the first day of the year in which they appear. This will have the effect of solving for a moderately smaller NPV.";
const sgAheDisclaimer =
  "Information contained in this report has been obtained from sources that we believe reliable. We assume, without independent investigation, that such information is complete and accurate. Investors are urged to consult with their own tax/legal advisors. AdviceOne Advisory Services LLC is not engaged in rendering tax or legal advice.";
const assetsDisclaimer =
  "This summary of accounts is believed to be accurate and should be used for informational use only. It should not be used in place of actual registration/ownership/beneficiary designation.";
const documentsDisclaimer =
  "This summary of documents is believed to be accurate and it is for informational use only.  This summary does not replace the actual legal documents drafted by your attorney. All legal advice should come from your attorney.";
const sarsPsuRsuDisclaimer =
  "Information contained in this report has been obtained from sources that we believe reliable, but no representation is made to its completeness or accuracy. AdviceOne Advisory Services, LLC is not engaged in rendering tax or legal advice. Accordingly, investors are urged to consult with their own tax/legal advisors.";
const plansDisclaimer =
  "This summary of accounts is believed to be accurate and should be used for informational use only. It should not be used in place of actual registration/ownership/beneficiary designation.";

const withDisclaimer = <P extends object>(WrappedComponent: React.ComponentType<P>) => {
  const WithExtraFooter: React.FC<WithExtraFooterProps & P> = (props) => {
    const { id } = useParams();
    const { data } = useUniquePossibilities(id!);
    const { pathname } = useLocation();

    const isBalanceSheet = pathname.includes("balance-sheet");
    const isUP = pathname.includes("unique-possibilities");
    const isNPV = pathname.includes("npv");
    const isSGAHE =
      pathname.includes("strategic-guide") || pathname.includes("accounts-held-elsewhere");
    const isAssets = pathname.includes("assets");
    const isDocuments = pathname.includes("documents");
    const isPlans = pathname.includes("plans");
    const isSarsPsuRsu =
      pathname.includes("stock-appreciation-rights") ||
      pathname.includes("restricted-stock-awards") ||
      pathname.includes("performance-stock-awards");

    return (
      <>
        <WrappedComponent {...props} />
        <div className="pt-12 text-center text-[8pt] italic text-primary">
          {isDocuments ? documentsDisclaimer : null}
          {isUP ? upDisclaimer : null}
          {isAssets ? assetsDisclaimer : null}
          {isBalanceSheet ? balDisclaimer : null}
          {isNPV ? npvDisclaimer : null}
          {isSGAHE ? sgAheDisclaimer : null}
          {isSarsPsuRsu ? sarsPsuRsuDisclaimer : null}
          {isPlans ? plansDisclaimer : null}
          <div className="mt-4 border-b border-t border-neutral-300 py-8 font-medium">
            Advisory Services offered through AdviceOne Advisory Services LLC, Registered Investment
            Advisor. {data?.household?.owner?.preferredFullName}, Investment Advisor Representative.
          </div>
        </div>
      </>
    );
  };

  return WithExtraFooter;
};
export default withDisclaimer;
